import { X, Plus } from 'lucide-react';
import { CSSProperties, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import SocketDebugTable from '@common/components/debug/socket-debug-table';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectIsDarkMode } from '@common-reducers/UserSelectors';

interface ExpandedViewProps {
  styles: Record<string, CSSProperties>;
  onClose: () => void;
  children: React.ReactNode;
  buttonId: string;
  onAddGateway: () => void;
}

export const ExpandedView = ({ styles, onClose, children, buttonId, onAddGateway }: ExpandedViewProps) => {
  const isInWhatsApp = window.location.href.includes('web.whatsapp.com');
  const [position, setPosition] = useState({ top: 0, right: 0 });
  const [isPositioned, setIsPositioned] = useState(false);
  const isDarkMode = useAppSelector(selectIsDarkMode);

  const updatePosition = useCallback(() => {
    if (isInWhatsApp) {
      const button = document.querySelector(`[data-connected-engines-button="${buttonId}"]`);
      if (button) {
        const rect = button.getBoundingClientRect();
        const top = rect.bottom + 8;
        const right = window.innerWidth - rect.right;
        // Only produce a new state object when the anchor actually
        // moved — returning prev lets React skip the re-render.
        setPosition(prev =>
          prev.top === top && prev.right === right ? prev : { top, right }
        );
        setIsPositioned(true);
      }
    } else {
      setIsPositioned(true);
    }
  }, [isInWhatsApp, buttonId]);

  useEffect(() => {
    updatePosition();
    window.addEventListener('resize', updatePosition);

    // rAF loop instead of a 100ms interval — pauses while the tab is
    // hidden, and updatePosition only setStates when the rect changed.
    // Only needed on the WhatsApp path where the portal is anchored to a
    // moving button; on the web path the resize listener is sufficient.
    let rafId = 0;
    if (isInWhatsApp) {
      const track = () => {
        updatePosition();
        rafId = requestAnimationFrame(track);
      };
      rafId = requestAnimationFrame(track);
    }

    return () => {
      window.removeEventListener('resize', updatePosition);
      cancelAnimationFrame(rafId);
    };
  }, [updatePosition, isInWhatsApp]);

  const expandedContent = (
    <div style={{
      ...styles.expandedView,
      ...(isInWhatsApp ? {
        position: 'fixed',
        top: `${position.top}px`,
        right: `${position.right}px`,
        zIndex: 999999,
        opacity: isPositioned ? 1 : 0,
        transition: 'opacity 0.15s ease-in-out',
      } : {})
    }}>
      <div style={styles.header}>
        <h3 style={styles.title}>WhatsApp Devices</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            style={styles.addButton}
            onClick={onAddGateway}
            title="Add New WhatsApp Gateway"
          >
            <Plus size={14} />
          </button>
          {/* <button
            style={styles.closeButton}
            onClick={onClose}
          >
            <X size={16} />
          </button> */}
        </div>
      </div>
      {<SocketDebugTable />}
      {children}
    </div>
  );

  if (isInWhatsApp) {
    return createPortal(expandedContent, document.body);
  }

  return expandedContent;
}; 